import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Users, Zap } from "lucide-react";
import { useRef } from "react";

const trustBadges = [
  { icon: Users, label: "Free to start — no credit card" },
  { icon: Zap, label: "Claude · Cursor · Cline · Windsurf" },
  { icon: Sparkles, label: "OpenAPI · Postman · GraphQL · Swagger" },
];

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col items-center overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32"
    >
      <motion.div
        className="absolute inset-0 pointer-events-none select-none"
        style={{ y: bgY, opacity: bgOpacity }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: [
              "radial-gradient(ellipse 90% 55% at 50% -5%, hsl(217 91% 60% / 0.06) 0%, transparent 65%)",
              "radial-gradient(ellipse 45% 45% at 92% 55%, hsl(162 70% 45% / 0.045) 0%, transparent 60%)",
            ].join(", "),
          }}
        />
      </motion.div>

      <div className="container relative z-10 px-6 max-w-3xl text-center space-y-10 md:space-y-12">
        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="text-4xl md:text-5xl lg:text-[3.35rem] font-bold tracking-tight leading-[1.12] text-foreground"
        >
          Convert Any API Spec to <span className="text-gradient">MCP</span> in Seconds
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed"
        >
          Paste an <strong className="text-foreground font-medium">OpenAPI</strong>,{" "}
          <strong className="text-foreground font-medium">Postman collection</strong>,{" "}
          <strong className="text-foreground font-medium">GraphQL schema</strong>, or Swagger spec
          and get a hosted, production-ready MCP server — no code required.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center"
        >
          <motion.a
            href="#try-now"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl btn-neu-primary text-base transition-all"
          >
            Try It Free
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </motion.a>
          <motion.a
            href="/registry"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl border border-border bg-background/80 hover:bg-accent text-base font-semibold text-foreground transition-all"
          >
            Browse Registry
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-xs md:text-sm text-muted-foreground pt-1"
        >
          {trustBadges.map((b) => (
            <span key={b.label} className="flex items-center gap-1.5">
              <b.icon className="w-3.5 h-3.5 text-primary/70" />
              {b.label}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
